import {useContext, useEffect, useState} from 'react';
import Block from '../block';
import {WebsiteContext} from '../../index';
import {isAllLocationsGroupKey, resolveReviewsLinkAction} from './link-action-rules';
import {GOOGLE_REVIEWS_SETUP_MESSAGE, GOOGLE_REVIEWS_SETUP_STATES} from './google-reviews-setup-state';
import {fetchJsonSharedInFlight} from '../../utils/reviews-request-cache';

const resolveCustomHref = (value) => {
    const href = String(value || '').trim();
    if (!href) return null;
    if (/^javascript:/i.test(href)) return null;
    return href;
};

export default function ReviewsSummaryBlock(props) {
    const [summary, setSummary] = useState(null);
    const context = useContext(WebsiteContext);
    const isBuilder = !!context?.builder;
    const isPicker = !!context?.picker;
    const showPlaceholder = isBuilder || isPicker;
    const isEditor = isBuilder || isPicker;
    const reviewsSetupState = String(context?.reviewsSetup?.google || GOOGLE_REVIEWS_SETUP_STATES.unknown).trim().toLowerCase();
    const showSetupWarning = isEditor && reviewsSetupState === GOOGLE_REVIEWS_SETUP_STATES.notConfigured;
    const isCheckingSetup = !!context?.reviewsSetupLoading?.google;

    const provider = props.values?.provider || 'places';
    const mode = props.values?.mode || 'group';
    const scope = props.values?.scope || 'website';
    const groupKey = props.values?.groupKey || 'all';
    const sourceKey = props.values?.sourceKey;
    const sourceKeys = Array.isArray(props.values?.sourceKeys)
        ? props.values.sourceKeys.filter(Boolean)
        : [];
    const sourceKeysToken = sourceKeys.join('|');
    const precision = props.values?.precision ?? 1;
    const showCount = props.values?.showCount ?? true;
    const copyStyle = String(
        props.values?.copyStyle
        || props.values?.copy
        || props.values?.suffix
        || 'google-reviews'
    ).trim().toLowerCase();
    const customCopyText = String(props.values?.copyText || props.values?.suffixText || '').trim();
    const customHref = resolveCustomHref(props.values?.customUrl);
    const variant = props.values?.variant || 'compact';

    useEffect(() => {
        if (!provider) return;
        if (showSetupWarning) {
            setSummary(null);
            return;
        }

        const controller = new AbortController();

        const fetchSummary = async () => {
            try {
                let nextSummary;
                if (mode === 'sources') {
                    nextSummary = await fetchJsonSharedInFlight({
                        url: `/api/v2/reviews/group/summary?provider=${encodeURIComponent(provider)}`,
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: sourceKeys,
                        signal: controller.signal
                    });
                } else {
                    const url = mode === 'single'
                        ? `/api/v2/reviews/summary?provider=${encodeURIComponent(provider)}&sourceKey=${encodeURIComponent(sourceKey || '')}`
                        : `/api/v2/reviews/group/${encodeURIComponent(groupKey)}/summary?provider=${encodeURIComponent(provider)}&scope=${encodeURIComponent(scope)}`;

                    nextSummary = await fetchJsonSharedInFlight({
                        url,
                        signal: controller.signal
                    });
                }
                setSummary(nextSummary);
            } catch (e) {
                if (e.name !== 'AbortError') console.log(e);
            }
        };

        if (mode === 'single' && !sourceKey) return;
        if (mode === 'sources' && sourceKeys.length === 0) return;
        fetchSummary();
        return () => controller.abort();
    }, [provider, mode, scope, groupKey, sourceKey, sourceKeysToken, showSetupWarning]);

    if (showSetupWarning) {
        const onRecheck = () => {
            if (typeof context?.onRecheckGoogleReviewsSetup === 'function') {
                context.onRecheckGoogleReviewsSetup();
                return;
            }

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('cx:google-reviews-settings-updated'));
            }
        };

        return (
            <Block {...props} className={props.className}>
                <div className="mx-auto max-w-7xl section">
                    <div className="inline-flex flex-col rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <div>{GOOGLE_REVIEWS_SETUP_MESSAGE}</div>
                        <button
                            type="button"
                            className="mt-1 inline-flex w-fit items-center rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={onRecheck}
                            disabled={isCheckingSetup}
                        >
                            {isCheckingSetup ? 'Rechecking...' : 'Recheck'}
                        </button>
                    </div>
                </div>
            </Block>
        );
    }

    const fallbackSummary = showPlaceholder ? {rating: 4.8, count: 128, mapsUrl: null} : null;
    const resolved = summary ?? fallbackSummary;
    if (!resolved) return null;

    const rating = resolved?.rating ?? 0;
    const count = resolved?.count ?? 0;
    const mapsUrl = resolved?.mapsUrl;
    const sourceCount = (() => {
        if (mode === 'single') return sourceKey ? 1 : 0;
        if (mode === 'sources') return sourceKeys.length;
        if (Number.isSafeInteger(resolved?.sourceCount) && resolved.sourceCount >= 0) {
            return resolved.sourceCount;
        }
        const sourceMap = resolved?.sources;
        return sourceMap && typeof sourceMap === 'object' ? Object.keys(sourceMap).length : 0;
    })();
    const isAllLocationsGroup = mode === 'group' && isAllLocationsGroupKey(groupKey);
    const actionState = resolveReviewsLinkAction({
        configuredLinkTarget: props.values?.linkTarget,
        legacyLinkEnabled: props.values?.link !== false,
        selectionCount: sourceCount,
        isAllLocationsGroup
    });
    const linkTarget = actionState.nextLinkTarget;

    const formatRating = (value) => {
        const p = typeof precision === 'number' ? precision : 1;
        return Number(value || 0).toFixed(p);
    };

    const formatCount = (value, short) => {
        const tidy = (num) => {
            const fixed = Number(num).toFixed(1);
            return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
        };
        if (!short) return Number(value || 0).toLocaleString();
        const v = Number(value || 0);
        if (v >= 1000000) return `${tidy(v / 1000000)}m`;
        if (v >= 1000) return `${tidy(v / 1000)}k`;
        return v.toLocaleString();
    };

    const resolvedVariant = variant === 'compact' ? 'inline' : variant;
    const fillPercent = Math.max(0, Math.min(100, (Number(rating || 0) / 5) * 100));
    const starsClipPath = 'polygon(8.82% 0.00%,6.34% 34.92%,0.00% 38.07%,4.82% 62.98%,3.37% 100.00%,8.82% 80.35%,14.28% 100.00%,12.82% 62.98%,17.65% 38.07%,11.31% 34.92%,8.82% 0.00%,29.41% 0.00%,26.93% 34.92%,20.59% 38.07%,25.41% 62.98%,23.96% 100.00%,29.41% 80.35%,34.87% 100.00%,33.41% 62.98%,38.24% 38.07%,31.90% 34.92%,29.41% 0.00%,50.00% 0.00%,47.51% 34.92%,41.18% 38.07%,46.00% 62.98%,44.54% 100.00%,50.00% 80.35%,55.46% 100.00%,54.00% 62.98%,58.82% 38.07%,52.49% 34.92%,50.00% 0.00%,70.59% 0.00%,68.10% 34.92%,61.76% 38.07%,66.59% 62.98%,65.13% 100.00%,70.59% 80.35%,76.04% 100.00%,74.59% 62.98%,79.41% 38.07%,73.07% 34.92%,70.59% 0.00%,91.18% 0.00%,88.69% 34.92%,82.35% 38.07%,87.18% 62.98%,85.72% 100.00%,91.18% 80.35%,96.63% 100.00%,95.18% 62.98%,100.00% 38.07%,93.66% 34.92%,91.18% 0.00%)';
    const chipStyle = {
        display: 'inline-block',
        maxWidth: '100%',
        font: 'inherit',
        color: 'inherit',
        lineHeight: 1,
        textAlign: 'inherit',
        ...(resolvedVariant === 'pill'
            ? {
                padding: '2px 8px',
                borderRadius: '999px',
                background: 'var(--reviews-chip-pill-bg,rgba(255,255,255,0.82))',
                border: '1px solid var(--reviews-chip-pill-border,rgba(0,0,0,0.24))'
            }
            : {})
    };
    const ratingStyle = {fontVariantNumeric: 'tabular-nums', fontWeight: 600, flex: '0 0 auto'};
    const contentStyle = {
        display: 'flex',
        alignItems: 'center',
        columnGap: '0.35em',
        rowGap: '0.14em',
        flexWrap: 'wrap',
        maxWidth: '100%',
        lineHeight: 1
    };
    const primaryClusterStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        columnGap: '0.35em',
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
        lineHeight: 1
    };
    const secondaryClusterStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1,
        whiteSpace: 'normal',
        minWidth: 0,
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        flex: '0 1 auto'
    };
    const starsStyle = {
        display: 'inline-block',
        flex: '0 0 auto',
        width: '5.05em',
        minWidth: '5.05em',
        maxWidth: '5.05em',
        height: '0.85em',
        lineHeight: 0,
        transform: 'translateY(-0.03em)',
        clipPath: starsClipPath,
        background: `linear-gradient(90deg,var(--reviews-chip-star-fill,#f4b400) ${fillPercent}%,var(--reviews-chip-star-empty,#d1d5db) ${fillPercent}%)`,
        filter: 'var(--reviews-chip-star-filter,none)'
    };
    const countStyle = {
        opacity: 'var(--reviews-chip-count-opacity,0.82)',
        fontSize: '0.9em',
        lineHeight: 1,
        whiteSpace: 'normal',
        minWidth: 0,
        overflowWrap: 'anywhere',
        display: 'inline-flex',
        alignItems: 'center'
    };
    const suffixText = (() => {
        if (copyStyle === 'on-google') return 'on Google';
        if (copyStyle === 'custom') return customCopyText;
        if (copyStyle === 'none') return '';
        return 'Google reviews';
    })();
    const countText = showCount ? formatCount(count, props.values?.shortCount ?? true) : '';
    const secondaryText = [countText, suffixText].filter(Boolean).join(' ');
    const linkHref = (() => {
        if (linkTarget === 'custom') return customHref;
        if (linkTarget === 'google' && mapsUrl && actionState.isGoogleEligible) return mapsUrl;
        return null;
    })();
    const WrapperElement = linkHref ? 'a' : 'span';
    const wrapperProps = linkHref
        ? {
            href: linkHref,
            ...(linkTarget === 'google' ? {target: '_blank', rel: 'noreferrer'} : {}),
            className: 'no-underline'
        }
        : {};

    const hasPrimaryCluster = true;
    const content = (
        <span style={contentStyle}>
            {hasPrimaryCluster ? (
                <span style={primaryClusterStyle}>
                    <span style={ratingStyle}>{formatRating(rating)}</span>
                    <span aria-hidden="true" style={starsStyle}></span>
                </span>
            ) : null}
            {secondaryText ? (
                <span style={secondaryClusterStyle}>
                    <span style={countStyle}>
                        {secondaryText}
                    </span>
                </span>
            ) : null}
        </span>
    );

    return (
        <Block {...props} className={props.className}>
            <div className="mx-auto max-w-7xl section">
                <WrapperElement style={chipStyle} {...wrapperProps}>{content}</WrapperElement>
            </div>
        </Block>
    );
}
